import { useEffect, useState } from "react";
import {
  App,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from "antd";
import dayjs from "dayjs";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../../lib/firebase";
import type { TaskItem, TaskStatus } from "../../lib/types";
import { taskStatusOptions, type UserOption } from "./types";

interface SubtaskFormModalProps {
  open: boolean;
  parentTask: TaskItem | null;
  users: UserOption[];
  onCancel: () => void;
  onSaved?: () => void;
}

interface SubtaskFormState {
  title: string;
  description: string;
  dependencies: string;
  status: TaskStatus;
  assignedTo: string;
  startDate: string;
  dueDate: string;
}

const emptyForm = (startDate: string): SubtaskFormState => ({
  title: "",
  description: "",
  dependencies: "",
  status: "todo",
  assignedTo: "",
  startDate,
  dueDate: "",
});

/**
 * Shared inline modal for creating a Subtask under a parent Task.
 * Auto-prefills startDate from the parent Task's startDate (no live link
 * after save — user can edit independently).
 */
export default function SubtaskFormModal({
  open,
  parentTask,
  users,
  onCancel,
  onSaved,
}: SubtaskFormModalProps) {
  const { message } = App.useApp();
  const [form, setForm] = useState<SubtaskFormState>(emptyForm(""));
  const [saving, setSaving] = useState(false);

  // Reset form whenever the modal opens for a (possibly new) parent task,
  // prefilling startDate with the parent Task's startDate.
  useEffect(() => {
    if (open && parentTask) {
      setForm(emptyForm(parentTask.startDate || ""));
    }
  }, [open, parentTask]);

  const handleSave = async () => {
    if (!parentTask || !form.title.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "taskSubtasks"), {
        projectId: parentTask.projectId,
        taskId: parentTask.id,
        title: form.title.trim(),
        description: form.description?.trim() || "",
        status: form.status || "todo",
        dependencies: form.dependencies?.trim() || "",
        startDate: form.startDate || null,
        dueDate: form.dueDate || null,
        assignedTo: form.assignedTo || null,
        completedAt:
          form.status === "done" ? new Date().toISOString() : null,
        comments: [],
        createdAt: new Date().toISOString(),
      });
      message.success("Subtask created.");
      onSaved?.();
      onCancel();
    } catch (error) {
      console.error("Failed to create subtask:", error);
      message.error("Failed to create subtask. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        parentTask
          ? `New Subtask for "${parentTask.title}"`
          : "New Subtask"
      }
      open={open}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Create"
      okButtonProps={{
        disabled: !form.title.trim() || !parentTask,
        loading: saving,
      }}
      destroyOnClose
    >
      <Form layout="vertical">
        <Form.Item label="Subtask title" required>
          <Input
            value={form.title}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, title: event.target.value }))
            }
            placeholder="Subtask title"
          />
        </Form.Item>
        <Form.Item label="Description">
          <Input.TextArea
            value={form.description}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            autoSize={{ minRows: 2 }}
          />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Status">
              <Select
                value={form.status}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, status: value }))
                }
                options={taskStatusOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Assign to">
              <Select
                value={form.assignedTo || undefined}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, assignedTo: value || "" }))
                }
                allowClear
                placeholder="Free text or pick a user"
                options={users.map((userItem) => ({
                  value: userItem.uid,
                  label: userItem.displayName || userItem.email,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Start date"
              tooltip="Defaults to the parent Task's start date"
            >
              <DatePicker
                value={form.startDate ? dayjs(form.startDate) : null}
                onChange={(date) =>
                  setForm((prev) => ({
                    ...prev,
                    startDate: date ? date.format("YYYY-MM-DD") : "",
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Due date">
              <DatePicker
                value={form.dueDate ? dayjs(form.dueDate) : null}
                onChange={(date) =>
                  setForm((prev) => ({
                    ...prev,
                    dueDate: date ? date.format("YYYY-MM-DD") : "",
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Dependencies">
          <Input
            value={form.dependencies}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                dependencies: event.target.value,
              }))
            }
            placeholder="Free text"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
